/**
 * Where closing a work order goes back to. By default a work order closes to
 * its store's dashboard; a page that opens one can name itself instead (a
 * customer's page, say), so closing goes back where the person came from.
 *
 * The return place travels in the address (`&back=customer:co=…&id=…&for=<wo>`),
 * so a reload or a shared link keeps it. It is tied to the work order it was
 * set for (`for`): any other work order closes the default way, so a place
 * left in the address by some other navigation can never take over a close. Each kind of place registers how to go
 * back to it once, at start-up (see registerWorkOrderReturns in App); the work
 * order screen only asks returnFromWorkOrder() and knows none of them.
 */

/** The address parameter holding the return place. */
export const RETURN_PARAM = 'back';

/** A place to go back to: its kind and what that kind needs to get there. */
export interface WorkOrderReturn {
  kind: string;
  params: Record<string, string>;
}

/** Goes back to a place of one kind, given its params. */
export type WorkOrderReturnHandler = (params: Record<string, string>) => void;

const handlers = new Map<string, WorkOrderReturnHandler>();

/** Teaches the app how to go back to one kind of place. */
export function registerWorkOrderReturn(kind: string, handler: WorkOrderReturnHandler): void {
  handlers.set(kind, handler);
}

/** For tests: forget every registered kind. */
export function clearWorkOrderReturns(): void {
  handlers.clear();
}

const KIND = /^[a-z][a-z0-9_-]*$/;

/** The return place as the address carries it: `kind:a=1&b=2`. */
export function encodeWorkOrderReturn(r: WorkOrderReturn): string {
  if (!KIND.test(r.kind)) throw new Error(`invalid work order return kind: ${r.kind}`);
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(r.params)) if (v) params.set(k, v);
  return `${r.kind}:${params.toString()}`;
}

/** Reads a return place from the address value; null when absent or malformed. */
export function decodeWorkOrderReturn(raw: string | null | undefined): WorkOrderReturn | null {
  if (!raw) return null;
  const at = raw.indexOf(':');
  const kind = at < 0 ? raw : raw.slice(0, at);
  if (!KIND.test(kind)) return null;
  const params: Record<string, string> = {};
  new URLSearchParams(at < 0 ? '' : raw.slice(at + 1)).forEach((v, k) => {
    params[k] = v;
  });
  return { kind, params };
}

/** The param tying a return place to its work order. */
const FOR_PARAM = 'for';

/**
 * Sets the return place for work order `workOrderId` on address params, or
 * removes it (no place, or no work order). A malformed place is reported and
 * left out: the work order still opens, and closes the default way.
 */
export function setWorkOrderReturn(params: URLSearchParams, r: WorkOrderReturn | null | undefined, workOrderId?: string): void {
  params.delete(RETURN_PARAM);
  if (!r || !workOrderId) return;
  try {
    params.set(RETURN_PARAM, encodeWorkOrderReturn({ kind: r.kind, params: { ...r.params, [FOR_PARAM]: workOrderId } }));
  } catch (e) {
    console.error('Work order return place left out:', e);
  }
}

/**
 * Goes back to the place that opened the work order, when the address names
 * one the app knows. Returns false (and does nothing) otherwise, so the caller
 * falls back to its default.
 */
export function returnFromWorkOrder(search: string = window.location.search): boolean {
  const address = new URLSearchParams(search);
  const r = decodeWorkOrderReturn(address.get(RETURN_PARAM));
  const handler = r && handlers.get(r.kind);
  // Only for the work order it was set for.
  if (!r || !handler || !r.params[FOR_PARAM] || r.params[FOR_PARAM] !== address.get('wo')) return false;
  const { [FOR_PARAM]: _for, ...params } = r.params;
  handler(params);
  return true;
}
